import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api, errMsg } from "../lib/api";
import { Play, Clock, Star, Sparkles, ChevronRight, Search, PlusCircle, Trash2 } from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const DIFF_LABEL = { 1: "Iniciación", 2: "Básico", 3: "Intermedio", 4: "Avanzado", 5: "Examen" };

function fmtDuration(sec) {
  if (!sec) return "—";
  const m = Math.floor(sec / 60), s = sec % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

function LessonCard({ lesson, index }) {
  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.03, duration: 0.4 }}
                whileHover={{ y: -6, transition: { duration: 0.2 } }}
                className="relative group shrink-0 w-[280px]" data-testid="drive-lesson-card">
      <Link to={`/app/academia/drive/${lesson.id}`} className="block">
        <div className="relative aspect-video rounded-2xl overflow-hidden bg-neutral-800 shadow-lg shadow-black/40 border border-white/5 group-hover:border-orange-400/60 transition-colors">
          {lesson.thumbnail_url ? (
            <img src={lesson.thumbnail_url} alt="" className="absolute inset-0 h-full w-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500" />
          ) : (
            <div className="absolute inset-0 bg-gradient-to-br from-orange-500/40 via-neutral-900 to-black" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-transparent" />
          <div className="absolute top-3 left-3 flex gap-1.5">
            {lesson.premium && <span className="text-[9px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-gradient-to-r from-amber-400 to-orange-500 text-black">Premium</span>}
            {lesson.is_360 && <span className="text-[9px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-cyan-400/90 text-black">360°</span>}
          </div>
          <div className="absolute top-3 right-3 text-[10px] px-2 py-0.5 rounded-full bg-black/60 text-white/90 backdrop-blur-md flex items-center gap-1">
            <Clock size={10} /> {fmtDuration(lesson.duration_sec)}
          </div>
          <div className="absolute bottom-3 left-3 right-3">
            <p className="text-white font-semibold leading-tight line-clamp-2 drop-shadow">{lesson.title}</p>
            <div className="flex items-center gap-2 mt-1.5">
              <span className="text-[10px] uppercase tracking-wider text-orange-300">{lesson.category}</span>
              <span className="text-[10px] text-white/60">·</span>
              <span className="text-[10px] text-white/70 flex items-center gap-0.5">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} size={9} className={i < lesson.difficulty ? "fill-orange-400 text-orange-400" : "text-white/25"} />
                ))}
                <span className="ml-1">{DIFF_LABEL[lesson.difficulty]}</span>
              </span>
            </div>
          </div>
          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
            <div className="h-14 w-14 rounded-full bg-white/95 flex items-center justify-center shadow-2xl">
              <Play size={22} className="text-orange-600 ml-0.5" fill="currentColor" />
            </div>
          </div>
          {lesson.progress_pct > 0 && (
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/10">
              <div className="h-full bg-orange-500" style={{ width: `${lesson.progress_pct}%` }} />
            </div>
          )}
        </div>
      </Link>
    </motion.div>
  );
}

function CategoryProgress({ cat }) {
  const radius = 22, circ = 2 * Math.PI * radius;
  return (
    <div className="flex items-center gap-3 shrink-0 bg-white/5 backdrop-blur-md rounded-xl px-3 py-2 border border-white/10">
      <div className="relative h-14 w-14">
        <svg className="absolute inset-0" viewBox="0 0 60 60">
          <circle cx="30" cy="30" r={radius} fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="4" />
          <circle cx="30" cy="30" r={radius} fill="none" stroke="url(#g)" strokeWidth="4" strokeLinecap="round"
                  strokeDasharray={circ} strokeDashoffset={circ * (1 - cat.pct / 100)}
                  transform="rotate(-90 30 30)" />
          <defs>
            <linearGradient id="g" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#f97316" />
              <stop offset="100%" stopColor="#facc15" />
            </linearGradient>
          </defs>
        </svg>
        <div className="absolute inset-0 flex items-center justify-center text-xs font-bold text-white">{cat.pct}%</div>
      </div>
      <div className="min-w-0">
        <p className="text-[11px] text-white/50 uppercase tracking-wider">{cat.category}</p>
        <p className="text-xs text-white/80">{cat.completed}/{cat.lessons} completadas</p>
      </div>
    </div>
  );
}

function CreateLessonDialog({ categories, onClose, onCreated }) {
  const [form, setForm] = useState({
    title: "", description: "", category: categories[0] || "",
    difficulty: 3, duration_sec: 120, video_url: "", thumbnail_url: "",
    professor_video_url: "", interior_video_url: "", pedals_video_url: "",
    gear_video_url: "", speed_video_url: "",
    insta360_url: "", insta360_thumbnail_url: "",
    is_360: false, premium: false,
  });
  const [busy, setBusy] = useState(false);
  const [showTracks, setShowTracks] = useState(false);
  const submit = async () => {
    if (!form.title || !form.video_url || !form.category) {
      toast.error("Rellena título, categoría y enlace del vídeo principal"); return;
    }
    const pin = window.prompt("PIN Maestro para publicar la lección:");
    if (!pin) return;
    setBusy(true);
    try {
      await api.post("/drive/lessons", form, { headers: { "X-Master-Pin": pin } });
      toast.success("Lección publicada en Natur Drive 360");
      onCreated();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} data-testid="drive-create-dialog"
           className="w-full max-w-2xl bg-neutral-950 border border-white/10 rounded-2xl p-6 space-y-3 text-white shadow-2xl max-h-[92vh] overflow-y-auto">
        <h3 className="font-heading text-xl">Nueva lección Natur Drive 360</h3>
        <p className="text-xs text-white/50">Pega enlaces directos <code className="text-orange-300">.mp4?download=1</code> de OneDrive. El vídeo principal es obligatorio; el resto son pistas opcionales que se sincronizarán por <code>currentTime</code>.</p>
        <Input placeholder="Título" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
               className="bg-white/5 border-white/10 text-white" data-testid="drive-create-title" />
        <textarea placeholder="Descripción" rows={2} value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="w-full text-sm bg-white/5 border border-white/10 rounded-md p-2 text-white" data-testid="drive-create-desc" />
        <div className="grid grid-cols-3 gap-2">
          <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="h-10 rounded-md bg-white/5 border border-white/10 px-2 text-sm text-white" data-testid="drive-create-category">
            {categories.map((c) => <option key={c} className="text-black">{c}</option>)}
          </select>
          <select value={form.difficulty} onChange={(e) => setForm({ ...form, difficulty: parseInt(e.target.value) })}
                  className="h-10 rounded-md bg-white/5 border border-white/10 px-2 text-sm text-white">
            {[1, 2, 3, 4, 5].map((d) => <option key={d} value={d} className="text-black">{d} · {DIFF_LABEL[d]}</option>)}
          </select>
          <Input type="number" placeholder="Duración (s)" value={form.duration_sec}
                 onChange={(e) => setForm({ ...form, duration_sec: parseInt(e.target.value || 0) })}
                 className="bg-white/5 border-white/10 text-white" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-orange-300 mb-1 block">Vídeo principal (master · con audio) *</label>
          <Input placeholder="URL MP4 del vídeo de carretera" value={form.video_url}
                 onChange={(e) => setForm({ ...form, video_url: e.target.value })}
                 className="bg-white/5 border-white/10 text-white" data-testid="drive-create-video-url" />
        </div>
        <Input placeholder="URL de la miniatura (opcional)" value={form.thumbnail_url}
               onChange={(e) => setForm({ ...form, thumbnail_url: e.target.value })}
               className="bg-white/5 border-white/10 text-white" />

        <button type="button" onClick={() => setShowTracks((s) => !s)}
                className="text-[11px] uppercase tracking-widest text-white/60 hover:text-orange-300 transition"
                data-testid="drive-create-toggle-tracks">
          {showTracks ? "▾" : "▸"} Pistas multi-cámara sincronizadas (opcional)
        </button>
        {showTracks && (
          <div className="grid gap-2 pl-3 border-l border-white/10">
            {[
              ["professor_video_url", "🎓 Profesor (panel fijo)"],
              ["interior_video_url", "🚗 Vista interior"],
              ["pedals_video_url", "🦶 Pedales"],
              ["gear_video_url", "⚙ Marchas"],
              ["speed_video_url", "📈 Velocidad/cuadro"],
            ].map(([field, label]) => (
              <Input key={field} placeholder={`${label} — URL MP4 (opcional)`}
                     value={form[field]} onChange={(e) => setForm({ ...form, [field]: e.target.value })}
                     className="bg-white/5 border-white/10 text-white text-sm" data-testid={`drive-create-${field}`} />
            ))}
            <p className="text-[10px] text-white/40">Los paneles sin URL mostrarán su decoración animada actual con el badge DEMO.</p>
          </div>
        )}

        <div className="pt-2 border-t border-white/10">
          <label className="text-[10px] uppercase tracking-widest text-cyan-300 mb-1 block">🌐 Experiencia Insta360 360° (opcional)</label>
          <Input placeholder="URL de la experiencia publicada en Insta360" value={form.insta360_url}
                 onChange={(e) => setForm({ ...form, insta360_url: e.target.value })}
                 className="bg-white/5 border-white/10 text-white mb-1" data-testid="drive-create-insta360-url" />
          <Input placeholder="URL de la miniatura Insta360" value={form.insta360_thumbnail_url}
                 onChange={(e) => setForm({ ...form, insta360_thumbnail_url: e.target.value })}
                 className="bg-white/5 border-white/10 text-white" />
        </div>

        <div className="flex items-center gap-4 text-sm">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.is_360} onChange={(e) => setForm({ ...form, is_360: e.target.checked })} /> Vídeo 360°
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.premium} onChange={(e) => setForm({ ...form, premium: e.target.checked })} /> Premium
          </label>
        </div>
        <div className="flex gap-2 pt-2">
          <Button onClick={submit} disabled={busy} data-testid="drive-create-submit"
                  className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-black font-semibold rounded-full">
            {busy ? "Publicando…" : "Publicar lección"}
          </Button>
          <Button variant="ghost" onClick={onClose} className="text-white/70 hover:text-white">Cancelar</Button>
        </div>
      </div>
    </div>
  );
}

export default function DriveLibrary() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [lessons, setLessons] = useState([]);
  const [progress, setProgress] = useState(null);
  const [categories, setCategories] = useState([]);
  const [q, setQ] = useState("");
  const [activeCat, setActiveCat] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const canManage = user && ["SUPER_ADMIN", "FRANQUICIA", "PROFESOR"].includes(user.role);

  const load = async () => {
    try {
      const [lRes, pRes, cRes] = await Promise.all([
        api.get("/drive/lessons"),
        api.get("/drive/progress"),
        api.get("/drive/categories"),
      ]);
      setLessons(lRes.data); setProgress(pRes.data); setCategories(cRes.data);
    } catch (e) { toast.error(errMsg(e)); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    return lessons.filter((l) =>
      (!q || l.title.toLowerCase().includes(q.toLowerCase()) || (l.description || "").toLowerCase().includes(q.toLowerCase())) &&
      (!activeCat || l.category === activeCat));
  }, [lessons, q, activeCat]);

  const byCategory = useMemo(() => {
    const groups = {};
    filtered.forEach((l) => { (groups[l.category] = groups[l.category] || []).push(l); });
    return groups;
  }, [filtered]);

  const featured = useMemo(() => {
    return lessons.find((l) => l.progress_pct > 0 && l.progress_pct < 100) || lessons[0];
  }, [lessons]);

  return (
    <div className="min-h-[calc(100vh-3rem)] bg-[#050608] text-white" data-testid="drive-library">
      {/* Hero destacado */}
      {featured && (
        <div className="relative overflow-hidden">
          <div className="absolute inset-0">
            {featured.thumbnail_url && (
              <img src={featured.thumbnail_url} alt="" className="h-full w-full object-cover scale-110 blur-sm opacity-40" />
            )}
            <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-black/40" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#050608] via-transparent to-transparent" />
          </div>
          <div className="relative px-6 md:px-12 pt-10 pb-16 max-w-5xl">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-orange-400 font-bold">
              <Sparkles size={12} /> Natur Drive 360
            </div>
            <h1 className="mt-4 text-4xl md:text-6xl font-heading font-black leading-none tracking-tight">
              {featured.title}
            </h1>
            <p className="mt-4 text-white/70 max-w-2xl leading-relaxed">{featured.description}</p>
            <div className="flex items-center gap-4 mt-6">
              <Button data-testid="drive-hero-play"
                      onClick={() => nav(`/app/academia/drive/${featured.id}`)}
                      className="rounded-full bg-white text-black hover:bg-white/90 font-bold px-6 h-11">
                <Play size={16} className="mr-2" fill="currentColor" /> Ver lección
              </Button>
              <div className="text-xs text-white/50 flex items-center gap-3">
                <span className="uppercase tracking-widest">{featured.category}</span>
                <span>·</span>
                <span>{fmtDuration(featured.duration_sec)}</span>
                <span>·</span>
                <span>{DIFF_LABEL[featured.difficulty]}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Buscador + progreso */}
      <div className="px-6 md:px-12 space-y-6">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[240px] max-w-md">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <Input placeholder="Buscar por título, glorieta, autovía…"
                   value={q} onChange={(e) => setQ(e.target.value)}
                   className="pl-9 bg-white/5 border-white/10 text-white placeholder:text-white/40 h-11 rounded-full"
                   data-testid="drive-search-input" />
          </div>
          <div className="flex gap-1.5 overflow-x-auto pb-1">
            <button onClick={() => setActiveCat("")}
                    className={`text-[11px] uppercase tracking-wider px-3 py-1.5 rounded-full whitespace-nowrap transition ${!activeCat ? "bg-white text-black" : "bg-white/5 text-white/70 hover:bg-white/10"}`}>
              Todas
            </button>
            {categories.map((c) => (
              <button key={c} onClick={() => setActiveCat(c === activeCat ? "" : c)} data-testid={`drive-cat-${c}`}
                      className={`text-[11px] uppercase tracking-wider px-3 py-1.5 rounded-full whitespace-nowrap transition ${activeCat === c ? "bg-white text-black" : "bg-white/5 text-white/70 hover:bg-white/10"}`}>
                {c}
              </button>
            ))}
          </div>
          {canManage && (
            <Button onClick={() => setShowCreate(true)} data-testid="drive-create-open"
                    className="ml-auto rounded-full bg-orange-500 hover:bg-orange-600 text-black font-semibold h-10">
              <PlusCircle size={15} className="mr-1.5" /> Nueva lección
            </Button>
          )}
        </div>

        {progress && progress.total_lessons > 0 && (
          <div className="flex gap-3 overflow-x-auto pb-2">
            <div className="shrink-0 bg-gradient-to-br from-orange-500/20 to-amber-500/10 rounded-xl px-4 py-3 border border-orange-400/30">
              <p className="text-[10px] uppercase tracking-widest text-orange-300">Progreso global</p>
              <p className="text-2xl font-bold text-white">{progress.total_pct}%</p>
              <p className="text-[11px] text-white/60">{progress.total_completed}/{progress.total_lessons} lecciones</p>
            </div>
            {progress.by_category.map((c) => <CategoryProgress key={c.category} cat={c} />)}
          </div>
        )}

        {/* Filas por categoría */}
        <div className="space-y-8 pb-16">
          {Object.entries(byCategory).map(([cat, list]) => (
            <div key={cat}>
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-heading text-lg tracking-tight flex items-center gap-2">
                  {cat}
                  <span className="text-xs text-white/40 font-normal">({list.length})</span>
                </h2>
                <button onClick={() => setActiveCat(cat)} className="text-[11px] uppercase text-white/50 hover:text-orange-400 flex items-center gap-0.5">
                  Ver todas <ChevronRight size={11} />
                </button>
              </div>
              <div className="flex gap-4 overflow-x-auto pb-3 -mx-1 px-1">
                {list.map((l, i) => <LessonCard key={l.id} lesson={l} index={i} />)}
              </div>
            </div>
          ))}
          {Object.keys(byCategory).length === 0 && (
            <p className="text-center text-white/40 py-16">No hay lecciones que coincidan con tu búsqueda.</p>
          )}
        </div>
      </div>

      {showCreate && <CreateLessonDialog categories={categories} onClose={() => setShowCreate(false)}
                                          onCreated={() => { setShowCreate(false); load(); }} />}
    </div>
  );
}
